import * as React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { getGeneralSettings } from '../../../store/settings/selectors';
import { AnalysisResult } from './useAnalyseCSV';
import { ColumnMapping, CustomProperty, STANDARD_FIELDS, autoMap } from './autoMap';
import { applyStoredMapping } from './useMappingStorage';
import { ImportOptions } from './ImportOptions';
import UploadStep from './UploadStep';
import MappingStep, { MappingField } from './MappingStep';
import PreviewStep from './PreviewStep';
import ImportStep from './ImportStep';

type ModalStep = 'upload' | 'map' | 'preview' | 'import';

interface StepDef {
    id: ModalStep;
    labelKey: TK;
}

const STEPS: StepDef[] = [
    { id: 'upload',  labelKey: TK.importClientsStepUpload  },
    { id: 'map',     labelKey: TK.importClientsStepMap     },
    { id: 'preview', labelKey: TK.importClientsStepPreview },
    { id: 'import',  labelKey: TK.importClientsStepImport  },
];

interface ImportClientsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

function getFieldLabel(fieldId: string, t: (key: TK) => string): string {
    const map: Partial<Record<string, string>> = {
        name: t(TK.name),
        taxNumber: t(TK.taxNumber),
        healthNumber: t(TK.healthNumber),
        identificationNumber: t(TK.identificationNumber),
        clientFileNumber: t(TK.clientFileNumberLabel),
        phoneNumbers: t(TK.phoneNumbers),
        email: t(TK.email),
        sex: t(TK.sex),
        birthday: t(TK.birthdate),
        birthdayYear: t(TK.birthdayYear),
        birthdayMonth: t(TK.birthdayMonth),
        birthdayDay: t(TK.birthdayDay),
        job: t(TK.job),
        origin: t(TK.origin),
        subsystemName: t(TK.subsystemName),
        subsystemNumber: t(TK.subsystemNumber),
        addressLine1: `${t(TK.address)} 1`,
        addressLine2: `${t(TK.address)} 2`,
        zipCode: t(TK.zipCode),
        city: t(TK.city),
        notes: t(TK.notes),
        internalNotes: t(TK.internalNotes),
        warningNote: t(TK.warning),
        status: t(TK.status),
        rgpdApproved: t(TK.consentApproved),
        acceptMarktingCommunication: t(TK.acceptMarketingCommunication),
        tags: t(TK.clientTags),
    };
    return map[fieldId] ?? fieldId;
}

const ImportClientsModal: React.FC<ImportClientsModalProps> = ({ isOpen, onClose }) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);
    const [step, setStep] = React.useState<ModalStep>('upload');
    const [analysisResult, setAnalysisResult] = React.useState<AnalysisResult | null>(null);
    const [mapping, setMapping] = React.useState<ColumnMapping | null>(null);
    const [options, setOptions] = React.useState<ImportOptions>({ skipExistingOnly: false });

    React.useEffect(() => {
        if (!isOpen) {
            setStep('upload');
            setAnalysisResult(null);
            setMapping(null);
            setOptions({ skipExistingOnly: false });
        }
    }, [isOpen]);

    const customProperties = React.useMemo<CustomProperty[]>(() => {
        const props = generalSettings?.clientProperties ?? {};
        return Object.entries(props).map(([key, settings]) => ({
            key,
            description: settings.description,
        }));
    }, [generalSettings]);

    const allFields = React.useMemo<MappingField[]>(() => {
        const standard: MappingField[] = STANDARD_FIELDS.map(id => ({
            id,
            label: getFieldLabel(id, t),
        }));
        const custom: MappingField[] = customProperties.map(p => ({
            id: `property:${p.key}`,
            label: p.description || p.key,
        }));
        return [...standard, ...custom];
    }, [t, customProperties]);

    const handleAnalysisComplete = React.useCallback((result: AnalysisResult) => {
        console.log('Analysis complete:', result);
        setAnalysisResult(result);
        const initialMapping = autoMap(result, customProperties);
        console.log('Initial mapping:', initialMapping);
        const restoredMapping = applyStoredMapping(initialMapping, result.headers);
        setMapping(restoredMapping);
        setStep('map');
    }, [customProperties]);

    const currentStepIndex = STEPS.findIndex(s => s.id === step);
    const isNameMapped = (mapping?.fieldMap['name']?.length ?? 0) > 0;

    const goToStep = React.useCallback((target: ModalStep) => {
        // Going back to upload resets file state; the user will upload a new file
        if (target === 'upload') {
            setAnalysisResult(null);
            setMapping(null);
        }
        setStep(target);
    }, []);

    const stepIndicator = (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacer-M)',
            paddingBottom: 'var(--spacer-M)',
            borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
        }}>
            {STEPS.map((s, i) => {
                const isPast = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const circleColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)';
                const labelColor = isCurrent ? 'var(--appBlue)' : isPast ? 'var(--appGreen)' : 'var(--text-muted)';

                return (
                    <React.Fragment key={s.id}>
                        {i > 0 && (
                            <div style={{
                                flex: 1,
                                height: '2px',
                                marginTop: '13px', // vertically centres with the circle
                                background: isPast ? 'var(--appGreen)' : 'var(--appLigthGrey)',
                                transition: 'background 0.2s',
                            }} />
                        )}
                        <div
                            onClick={isPast && step !== 'import' ? () => goToStep(s.id) : undefined}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'var(--spacer-XXS)',
                                cursor: isPast && step !== 'import' ? 'pointer' : 'default',
                                minWidth: '64px',
                            }}
                        >
                            <div style={{
                                width: '28px',
                                height: '28px',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                background: circleColor,
                                color: isCurrent || isPast ? 'white' : 'var(--text-muted)',
                                transition: 'background 0.2s',
                                flexShrink: 0,
                            }}>
                                {i + 1}
                            </div>
                            <span style={{
                                fontSize: '0.72rem',
                                color: labelColor,
                                fontWeight: isCurrent ? 600 : 400,
                                whiteSpace: 'nowrap',
                                transition: 'color 0.2s',
                                textAlign: 'center',
                            }}>
                                {t(s.labelKey)}
                            </span>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );

    return (
        <Modal isOpen={isOpen} toggle={onClose} size="xl">
            <ModalHeader toggle={onClose}>{t(TK.importClients)}</ModalHeader>
            <ModalBody>
                {stepIndicator}
                {step === 'upload' && (
                    <UploadStep onComplete={handleAnalysisComplete} />
                )}
                {step === 'map' && analysisResult && mapping && (
                    <MappingStep
                        result={analysisResult}
                        mapping={mapping}
                        onMappingChange={setMapping}
                        fields={allFields}
                        options={options}
                        onOptionsChange={setOptions}
                    />
                )}
                {step === 'preview' && analysisResult && mapping && (
                    <PreviewStep
                        result={analysisResult}
                        mapping={mapping}
                        options={options}
                    />
                )}
                {step === 'import' && analysisResult && mapping && (
                    <ImportStep
                        result={analysisResult}
                        mapping={mapping}
                        onClose={onClose}
                        options={options}
                    />
                )}
            </ModalBody>
            {step === 'map' && (
                <ModalFooter>
                    {!isNameMapped && (
                        <span style={{
                            marginRight: 'auto',
                            color: 'var(--danger-color)',
                            fontSize: '0.85rem',
                        }}>
                            {t(TK.importClientsMappingRequired)}
                        </span>
                    )}
                    <Button color="success" disabled={!isNameMapped} onClick={() => setStep('preview')}>
                        {t(TK.next)}
                    </Button>
                </ModalFooter>
            )}
            {step === 'preview' && (
                <ModalFooter>
                    <Button color="secondary" onClick={() => goToStep('map')}>
                        {t(TK.back)}
                    </Button>
                    <Button color="success" onClick={() => setStep('import')}>
                        {t(TK.importClientsStartImport)}
                    </Button>
                </ModalFooter>
            )}
        </Modal>
    );
};

export default ImportClientsModal;
